/**
 * Google Maps Places Autocomplete
 * Autocomplete Places Field using Google Maps Places API
 * Resources:
 * This sample uses the Places Autocomplete widget to:
 * 1. Help the user select a place
 * 2. Retrieve the address components associated with that place
 * 3. Populate the form fields with those address components.
 * This sample requires the Places library, Maps JavaScript API.
 * Include the libraries=places parameter when you first load the API.
 * For example: <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&libraries=places">
 */

const setInputValue = (field, value) => (field ? (field.value = value) : null)

function getGoogleMapsAddressComponentValues(placeObject) {
  let streetNumberValue = ``
  let addressNameValue = ``
  let addressLine2Value = ``
  let cityValue = ``
  let postcodeValue = ``
  let stateValue = ``

  // Get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of placeObject.address_components) {
    /**
     * Returns
     * street_number,
     * route (address),
     * locality (suburb/city),
     * administrative_area_level_1 (state),
     * country,
     * postal_code
     */
    const componentType = component.types[0]

    switch (componentType) {
      case `street_number`: {
        streetNumberValue = component.long_name
        break
      }

      case `subpremise`: {
        addressLine2Value = component.long_name
        break
      }

      case `route`: {
        addressNameValue = component.long_name
        break
      }

      case `postal_code`: {
        postcodeValue = `${component.long_name}${postcodeValue}`
        break
      }

      case `postal_code_suffix`: {
        postcodeValue = `${postcodeValue}-${component.long_name}`
        break
      }
      case `locality`: {
        cityValue = component.long_name
        break
      }

      case `administrative_area_level_1`: {
        stateValue = component.long_name
        break
      }
    }
  }

  addressLine2Value = addressLine2Value || ``

  return {
    streetNumberValue,
    addressNameValue,
    addressLine2Value,
    postcodeValue,
    cityValue,
    stateValue,
  }
}

function setAddressUIFields(googleMapsAutocomplete, selectors) {
  // Get the place details from the autocomplete object.
  const place = googleMapsAutocomplete.getPlace()
  const { address1Field, address2Field, localityField, postalField, stateField } = selectors
  const {
    streetNumberValue,
    addressNameValue,
    addressLine2Value,
    cityValue,
    postcodeValue,
    stateValue,
  } = getGoogleMapsAddressComponentValues(place)

  setInputValue(address1Field, `${streetNumberValue} ${addressNameValue}`)
  setInputValue(address2Field, addressLine2Value)
  setInputValue(localityField, cityValue)
  setInputValue(postalField, postcodeValue)
  setInputValue(stateField, stateValue)

  /**
   * After filling the form with address components from the Autocomplete
   * prediction, set cursor focus on the second address line to encourage
   * entry of subpremise information such as apartment, unit, or floor number.
   */
  address2Field.focus()
}

function setHiddenAddressFields(googleMapsAutocompleteAddress, hiddenFields) {
  // Get the place details from the autocomplete object.
  const place = googleMapsAutocompleteAddress.getPlace()
  const {
    streetNumberValue,
    addressNameValue,
  } = getGoogleMapsAddressComponentValues(place)

  const { addressNumberHiddenField, addressNameHiddenField } = hiddenFields

  setInputValue(addressNumberHiddenField, streetNumberValue)
  setInputValue(addressNameHiddenField, addressNameValue)
}

export default function GoogleAddressAutocomplete(country) {
  const selectorsArr = [
    {
      address1Field: document.querySelector(`#street_address`),
      address2Field: document.querySelector(`#street_address_line_2`),
      localityField: document.querySelector(`#city_suburb`),
      postalField: document.querySelector(`#zip`),
      stateField: document.querySelector(`#state`),
      hiddenFields: {
        addressNumberHiddenField: document.querySelector(`#street_number`),
        addressNameHiddenField: document.querySelector(`#street_name`),
      },
    },
    {
      address1Field: document.querySelector(`#business_street_address`),
      address2Field: document.querySelector(`#business_street_address_line_2`),
      localityField: document.querySelector(`#business_city`),
      postalField: document.querySelector(`#business_postcode`),
      stateField: document.querySelector(`#business_state`),
      hiddenFields: {
        addressNumberHiddenField: document.querySelector(
          `#business_street_number`
        ),
        addressNameHiddenField: document.querySelector(`#business_street_name`),
      },
    },
  ]

  for (const selectors of selectorsArr) {
    /**
     * Create the autocomplete object, restricting the search
     * predictions to geographical location types.
     */
    const autocomplete = new window.google.maps.places.Autocomplete(
      selectors.address1Field,
      {
        types: [`geocode`],
      }
    )

    /**
     * Restrict addresses to be Australia only
     */
    autocomplete.setComponentRestrictions({
      country: country || `au`,
    })

    /**
     * Avoid paying for data that you don't need by restricting the set of
     * place fields that are returned to just the address components.
     */
    autocomplete.setFields([`address_component`])

    /**
     * When the user selects an address from the drop-down, populate the
     * Address fields in the form.
     */
    autocomplete.addListener(`place_changed`, () => {
      setAddressUIFields(autocomplete, selectors)
      setHiddenAddressFields(autocomplete, selectors.hiddenFields)
    })

    /**
     * Bug Fix: Placing an event listener on the Input Field to
     * Allow the user to use the keyboard to select an
     * autocompleted street address, without toggling the form back
     * to display the primary address
     */
    window.google.maps.event.addDomListener(
      selectors.address1Field,
      `keydown`,
      (event) => {
        if (event.keyCode === 13) {
          event.preventDefault()
        }
      }
    )

    /**
     * Bug Fix/UX Improvement: Adjust the Address Autocomplete attribute value from 'off' to 'new-password'
     * The original value is set by initialising Autocomplete()
     */
    window.google.maps.event.addDomListener(
      selectors.address1Field,
      `focus`,
      e => e.target.setAttribute(`autocomplete`, `new-password`)
    )
  }
}
